import { keyBy, uniqBy } from 'lodash';
import { useMemo } from 'react';
import { CostRateSourceType } from 'api/cost-rates';
import { MemberType } from 'api/models';
import { BillRateSourceType } from 'api/project';
import { useGetTeamsByProjectId } from 'hooks/use-idb';
import { useAccountProfile } from 'queries/account';
import { useNativeActiveOrInvitedUsersQuery } from 'queries/members';
import { usePlaceholderResolutionsQuery } from 'queries/placeholder';
import { useProjectUserTeamsQuery } from 'queries/project';
import { useRolesQuery } from 'queries/roles';
import {
  CustomBillRateLookup,
  RateInfoRowData,
  ReplacedUserRateInfo,
  TeamRateInfo,
  UserRateInfo,
} from '../types';
import { useProjectBillRates } from './use-project-bill-rates';
import { useProjectUserTeamsCostRate } from './use-project-cost-rates';

const EMPTY_ARRAY = [];

export const useUserTeamsRateInfo = ({
  projectId,
  rateCardId,
  rateCardVersionId,
  customBillRateMap,
  overrideBillRate,
}: {
  projectId: number;
  rateCardId: number | undefined;
  rateCardVersionId: number | undefined;
  customBillRateMap: CustomBillRateLookup | undefined;
  overrideBillRate: boolean | undefined;
}) => {
  const { data: account } = useAccountProfile();

  const { data: nativeActiveUsers = EMPTY_ARRAY, isLoading: isUsersLoading } =
    useNativeActiveOrInvitedUsersQuery();
  const teamsMap = useGetTeamsByProjectId([projectId]);
  const { data: roles = EMPTY_ARRAY, isLoading: isRolesLoading } =
    useRolesQuery();
  const { data: placeholderResolutionsMap, isLoading: isPlaceholdersLoading } =
    usePlaceholderResolutionsQuery();
  const { data: userTeamData, isLoading: isUserTeamsLoading } =
    useProjectUserTeamsQuery(projectId);

  const {
    roleBillLookUp,
    defaultRate,
    isLoading: isRoleBillLookupLoading,
  } = useProjectBillRates(rateCardId, rateCardVersionId);
  const { userLevelCostRateMap, roleLevelCostRateMap, isCostRatesLoading } =
    useProjectUserTeamsCostRate(projectId);

  const userLookup = useMemo(() => {
    if (isUsersLoading) {
      return undefined;
    }

    return keyBy(nativeActiveUsers, 'userId');
  }, [isUsersLoading, nativeActiveUsers]);
  const roleLookup = useMemo(() => {
    if (isRolesLoading) {
      return undefined;
    }

    return keyBy(roles, 'roleId');
  }, [isRolesLoading, roles]);

  const userTeamInfo: RateInfoRowData[] = useMemo(() => {
    if (!userLookup || !roleLookup || !placeholderResolutionsMap) {
      return [];
    }
    // Get all the resolved teams for the current project
    const resolvedTeams = Object.keys(
      placeholderResolutionsMap[projectId]?.mappings ?? {},
    );

    // Map team options
    const teamOptions: TeamRateInfo[] = (teamsMap.data?.[projectId] ?? [])
      .filter(
        (team) =>
          !resolvedTeams.includes(String(team.teamId)) &&
          team.teamType !== MemberType.External,
      )
      .map((team) => {
        const roleId = team.roleId ?? 0;
        const role = roleLookup[roleId] ?? null;
        const billForRoleDetails = roleBillLookUp?.[roleId];
        const rate =
          billForRoleDetails?.currencyMap?.[
            account?.currency?.currencyCode ?? ''
          ]?.billRate;
        const billRate = rate != null ? rate : defaultRate;

        const customBillRateId = `${team.teamId}_${BillRateSourceType.TEAM}`;
        const customBillRate = customBillRateMap?.[customBillRateId]?.rate;
        const updatedUser = customBillRateMap?.[customBillRateId]?.updatedBy;
        const updatedAt = customBillRateMap?.[customBillRateId]?.updatedAt;

        const costRate =
          roleLevelCostRateMap[`${team.roleId}_${CostRateSourceType.Role}`];

        const hourRate = {
          billRate:
            overrideBillRate && customBillRateMap ? customBillRate : billRate,
          currency: account?.currency.currencySymbol ?? '',
          currencyCode: account?.currency?.currencyCode,
        };

        return {
          sourceId: team.teamId,
          team,
          role,
          sourceType: BillRateSourceType.TEAM,
          overrideBillRate,
          hourRate,
          updatedUser,
          updatedAt,
          costRate,
        };
      });

    const projectUsers = uniqBy(userTeamData?.members ?? [], 'user.userId');
    // Get all the user options
    const userOptions: UserRateInfo[] = (projectUsers ?? [])
      .filter(
        (member) => !!userLookup[member.user.userId], // to filter out customer users
      )
      .map((member) => {
        const user = userLookup[member.user.userId];
        const role = roleLookup[user?.role?.roleId || 0];
        const roleId = user?.role?.roleId || 0;
        const billForRoleDetails = roleBillLookUp?.[roleId];
        const rate =
          billForRoleDetails?.currencyMap?.[
            account?.currency?.currencyCode ?? ''
          ]?.billRate;
        const billRate = rate != null ? rate : defaultRate;

        const customBillRateId = `${member.user.userId}_${BillRateSourceType.USER}`;
        const customBillRate = customBillRateMap?.[customBillRateId]?.rate;
        const updatedUser = customBillRateMap?.[customBillRateId]?.updatedBy;
        const updatedAt = customBillRateMap?.[customBillRateId]?.updatedAt;
        const costRate =
          userLevelCostRateMap[
            `${member.user.userId}_${CostRateSourceType.User}`
          ] ||
          roleLevelCostRateMap[
            `${member.role?.roleId}_${CostRateSourceType.Role}`
          ];

        const hourRate = {
          billRate:
            overrideBillRate && customBillRateMap ? customBillRate : billRate,
          currency: account?.currency.currencySymbol ?? '',
          currencyCode: account?.currency?.currencyCode,
        };

        return {
          sourceId: member.user.userId,
          sourceType: BillRateSourceType.USER,
          user,
          role,
          hourRate,
          updatedUser,
          updatedAt,
          overrideBillRate,
          costRate,
        };
      });

    // Get all the replaced user options
    const replacedUserOptions: ReplacedUserRateInfo[] = (
      userTeamData?.members ?? []
    )
      .filter(
        (member) => !!userLookup[member.user.userId] && member.referenceTeam,
      )
      .map((member) => {
        const user = userLookup[member.user.userId];
        const referenceTeam = member.referenceTeam!;
        const roleId =
          (member.referenceTeam ? member?.role?.roleId : user?.role?.roleId) ||
          0;
        const billForRoleDetails = roleBillLookUp?.[roleId];
        const rate =
          billForRoleDetails?.currencyMap?.[
            account?.currency?.currencyCode ?? ''
          ]?.billRate;
        const billRate = rate != null ? rate : defaultRate;

        const customBillRateId = `${referenceTeam.teamId}_${user.userId}_${BillRateSourceType.TEAM}`;
        const customBillRate = customBillRateMap?.[customBillRateId]?.rate;
        const updatedUser = customBillRateMap?.[customBillRateId]?.updatedBy;
        const updatedAt = customBillRateMap?.[customBillRateId]?.updatedAt;
        const costRate =
          userLevelCostRateMap[
            `${member.user.userId}_${CostRateSourceType.User}`
          ] ||
          roleLevelCostRateMap[
            `${member.role?.roleId}_${CostRateSourceType.Role}`
          ];

        const hourRate = {
          billRate:
            overrideBillRate && customBillRateMap ? customBillRate : billRate,
          currency: account?.currency.currencySymbol ?? '',
          currencyCode: account?.currency?.currencyCode,
        };

        return {
          sourceId: referenceTeam.teamId,
          sourceType: BillRateSourceType.TEAM,
          user,
          role: member.role,
          referenceTeam,
          hourRate,
          updatedUser,
          updatedAt,
          overrideBillRate,
          costRate,
        };
      });

    return [...userOptions, ...replacedUserOptions, ...teamOptions];
  }, [
    account?.currency?.currencyCode,
    account?.currency.currencySymbol,
    customBillRateMap,
    defaultRate,
    overrideBillRate,
    placeholderResolutionsMap,
    projectId,
    roleBillLookUp,
    roleLevelCostRateMap,
    roleLookup,
    teamsMap.data,
    userLevelCostRateMap,
    userLookup,
    userTeamData?.members,
  ]);

  return {
    isRoleBillLookupLoading,
    isCostRatesLoading,
    isInfoLoading:
      isUserTeamsLoading ||
      isUsersLoading ||
      isRolesLoading ||
      isPlaceholdersLoading,
    userTeamInfo,
  };
};
